import { observer } from 'mobx-react-lite'
import WasThisHelpfullStore, { useWasThisHelpfullStore, WasThisHelpfullStoreProvider } from './WasThisHelpfullStore'
import useStore from 'reusable/mobx-store-utils'
import useInitialMount from 'reusable/hooks/useInitialMount'
import { feedbackCache } from 'cache/feedbackCache'
import Box, { BoxProps } from '@mui/material/Box'
import { themeConfig } from '@gap/shared/theme'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined'
import Button from '@mui/material/Button'
import ThumbDownOutlined from '@mui/icons-material/ThumbDownOutlined'
import GapDialog from 'reusable/gap-dialog/GapDialog'
import TextField from '@mui/material/TextField'
import FlexAlignRight from 'reusable/mui/utilities/FlexAlignRight'
import Stack from '@mui/material/Stack'
import { AnimatePresence, motion } from 'framer-motion'
import { ulid } from 'ulid'
import { FeedbackTypeEnum, feedbackQuestionsSchema, FeedbackQuestionsEnum } from '@gap/backend/app-services/feedback/models/FeedbackTypeEnum'
import type { MeetingAnalyticsParams } from 'analytics/MeetingAnalytics'

export interface WasThisHelpfullProps {
   /** Type of analytics event that will be triggered, linked to FeedbackAnalytics */
   type: FeedbackTypeEnum
   /** The id of the entity that is being evaluated, example, docId */
   entityId: string
   /** Overwrite the default text */
   text?: string
   /** Skip the initial question and go straight to the improve dialog */
   skipToImproveDialog?: boolean
   onClose?: () => void
   /** When set, thumbs up/down also emits the `Meeting AI feedback given` event */
   aiFeedback?: {
      feedbackTarget: MeetingAnalyticsParams.AiFeedbackGiven['feedbackTarget']
      agentName: MeetingAnalyticsParams.AiFeedbackGiven['agentName']
   }
}

export default observer(function WasThisHelpfull(props: WasThisHelpfullProps) {
   const wasThisHelpfullStore = useStore(sp => new WasThisHelpfullStore(sp), props)

   useInitialMount(() => {
      const submittedFeedbackIds = feedbackCache.documentSummary.get() || []
      wasThisHelpfullStore.canSubmitFeedback = !submittedFeedbackIds.includes(props.entityId)
   })

   return <WasThisHelpfullStoreProvider store={wasThisHelpfullStore}>
      <ImproveDialog />
      {!props.skipToImproveDialog && <Box sx={{ mt: 1, position: 'relative' }}>
         <AnimatePresence mode="wait" initial={false}>
            {wasThisHelpfullStore.canSubmitFeedback && <MotionBox>
               <AnimatePresence mode="wait" initial={false}>
                  {!wasThisHelpfullStore.hasSubmittedFeedback && <MotionBox sx={{ display: 'flex', alignItems: 'center' }}>
                     <Typography variant="caption" sx={{ color: themeConfig.primary[600] }}>
                        {props.text || 'How did we do?'}
                     </Typography>
                     <IconButton sx={{ mt: '-2px' }} onClick={wasThisHelpfullStore.submitGoodFeedback}>
                        <ThumbUpOutlined fontSize="extra-small" />
                     </IconButton>
                     <IconButton sx={{ mt: '-2px' }} onClick={wasThisHelpfullStore.submitBadFeedback}>
                        <ThumbDownOutlined fontSize="extra-small" />
                     </IconButton>
                  </MotionBox>}

                  {wasThisHelpfullStore.hasSubmittedFeedback && <MotionBox>
                     <Typography variant="caption" sx={{ color: themeConfig.primary[600] }}>
                        Thanks!
                     </Typography>

                     <Button
                        variant='text'
                        size='extra-small'
                        sx={{ color: themeConfig.primary[600], fontWeight: 700 }}
                        onClick={wasThisHelpfullStore.improveDialog.open}>
                        Tell us how we can improve
                     </Button>
                  </MotionBox>}
               </AnimatePresence>
            </MotionBox>}

            {!wasThisHelpfullStore.canSubmitFeedback && <MotionBox>
               <Typography variant="caption" sx={{ color: themeConfig.primary[600] }}>
                  Thanks!
               </Typography>
            </MotionBox>}
         </AnimatePresence>
      </Box>}
   </WasThisHelpfullStoreProvider>
})

const MotionBox = function MotionBox(props: BoxProps) {
   return <Box
      component={motion.div}
      key={ulid()}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      {...props}
   />
}

const ImproveDialog = observer(function ImproveDialog() {
   const wasThisHelpfullStore = useWasThisHelpfullStore()
   const questions = feedbackQuestionsSchema[wasThisHelpfullStore.sp.type]

   return <GapDialog modalState={wasThisHelpfullStore.improveDialog} size="small" title="Tell us how we can improve">
      <Stack spacing={2}>
         {questions.map((key) => {
            const label = FeedbackQuestionsEnum[key]
            return <Box key={key}>
               {wasThisHelpfullStore.formHandler[key].bindInput(ip =>
                  <TextField
                     {...ip}
                     label={label}
                     fullWidth
                     multiline
                     rows={2}
                  />)}
            </Box>
         })}

         <FlexAlignRight>
            <Button variant="contained" onClick={wasThisHelpfullStore.submitImprovement}>Submit</Button>
         </FlexAlignRight>
      </Stack>
   </GapDialog>
})
